import { useCallback, useEffect, useMemo } from 'react';
import { Alert, Button, InputGroup, Select, Text } from '@george-labs.com/design-system';
import type { ItemPropTypes } from '@george-labs.com/design-system/types/components/Dropdown';
import type { PropTypes } from '@george-labs.com/design-system/types/components/Select/SelectPropTypes';

import { t } from '@luna/i18n';
import type { Country } from '@shared/info-api';
import { useCountries } from '@shared/info-api';

import type { ContactItemPropTypes } from './ContactSelectService';
import {
    filterDefinedContacts,
    getPlaceHolderText,
    getSelectedContactId,
    getSelectedContacts,
    hasSomeDefinedContact,
    hasSomeUndefinedContact,
    isSelectedContactInvalid,
} from './ContactSelectService';
import { ContactSelectItem } from './ContactSelectItem';
import type { Contact } from '../ContactTypes';
import { getContactByIdFromContacts } from '../ContactService';

export type OmittedPropsFromSelect =
    | 'isLoading'
    | 'placeholder'
    | 'renderItem'
    | 'onSelect'
    | 'items';

export type Props<T> = Omit<PropTypes, OmittedPropsFromSelect> & {
    contacts: T[];
    isLoading: boolean;
    hasError: boolean;
    isNewContactAllowed: boolean;
    invalidContactText: string;
    invalidContactBtnLabel: string;
    addNewContactHref: string | undefined;
    preselectPrimaryContact: boolean | undefined;
    onAddNeContactClick?(): void;
    onSelect(contacts: T[]): void;
    convertContactsToSelectItems(contacts: T[], countries: Country[]): ContactItemPropTypes[];
    fetchContacts(): void;
};

export const ContactSelect = <T extends Contact>({
    value,
    disabled,
    contacts,
    isLoading,
    hasError,
    isNewContactAllowed,
    invalidContactText,
    invalidContactBtnLabel,
    addNewContactHref,
    addNewContactText,
    addNewContactBtnLabel,
    preselectPrimaryContact,
    onAddNeContactClick,
    onSelect,
    convertContactsToSelectItems,
    fetchContacts,
    ...props
}: Props<T>) => {
    const { data: countries = [] } = useCountries();

    const contactItems = useMemo(
        () => convertContactsToSelectItems(filterDefinedContacts(contacts), countries),
        [countries, contacts, convertContactsToSelectItems],
    );

    const handleSelect = useCallback(
        (item: ItemPropTypes | ItemPropTypes[] | null) => {
            const selectedContacts = getSelectedContacts(contacts, item);
            onSelect(selectedContacts);
        },
        [contacts, onSelect],
    );

    const selectedContactId = getSelectedContactId(
        value,
        contacts,
        isLoading,
        preselectPrimaryContact,
    );

    useEffect(() => {
        if (!selectedContactId || value === selectedContactId) {
            return;
        }
        onSelect([getContactByIdFromContacts(selectedContactId, contacts)!]);
    }, [contacts, selectedContactId, value, onSelect]);

    return (
        <>
            <InputGroup
                append={
                    (isNewContactAllowed || hasSomeUndefinedContact(contacts)) && (
                        <Button
                            href={addNewContactHref}
                            onClick={onAddNeContactClick}
                            data-cy="create-new-contact-btn"
                            variant={Button.VARIANT.SECONDARY}
                            size={Button.SIZE.SMALL}
                        >
                            {t('contactDataManagement.contactSelect.addBtnLink')}
                        </Button>
                    )
                }
            >
                <Select
                    {...props}
                    usePortal
                    isLoading={isLoading}
                    placeholder={getPlaceHolderText(contacts, isLoading, hasError)}
                    disabled={!hasSomeDefinedContact(contacts, isLoading) || disabled}
                    value={selectedContactId}
                    renderItem={({ item }) => <ContactSelectItem item={item} />}
                    onSelect={handleSelect}
                    items={contactItems}
                    dropdownClassName="cy-contact-select-dropdown"
                />
            </InputGroup>
            {hasError && (
                <Alert
                    size={Alert.SIZE.SMALL}
                    variant={Alert.VARIANT.ERROR}
                    buttons={[
                        <Button
                            key="refetch-contacts-btn"
                            data-cy="refetch-contacts-btn"
                            onClick={fetchContacts}
                        >
                            {t(
                                'contactDataManagement.contactSelect.contactsFetchErrorRefetchButton',
                            )}
                        </Button>,
                    ]}
                    data-cy="fetch-contacts-error"
                >
                    <Text>
                        {t('contactDataManagement.contactSelect.contactsFetchErrorDescription')}
                    </Text>
                </Alert>
            )}
            {isSelectedContactInvalid(contacts, selectedContactId) && (
                <Alert
                    size={Alert.SIZE.SMALL}
                    variant={Alert.VARIANT.WARNING}
                    buttons={[
                        <Button
                            key="invalid-contacts-btn"
                            data-cy="invalid-contacts-btn"
                            href="#/settings/contacts"
                        >
                            {invalidContactBtnLabel}
                        </Button>,
                    ]}
                    data-cy="invalid-contacts-warning"
                >
                    <Text>{invalidContactText}</Text>
                </Alert>
            )}
        </>
    );
};
